import { apiRoutes as builderApiRoutes } from '@features/campaigns/builder/consts/apiRoutes';
import { Item } from '@features/campaigns/builder/services/formFields';
import { apiClient } from '@shared/api/ApiClient';
import { AdManagerApiResponse } from '@shared/api/commonApiInterfaces';
import { apiRoutes as sharedApiRoutes } from '@shared/consts/apiRoutes';
import { UserId } from '@shared/types/users';

const ADVERTISER_FILTER_PAGE_SIZE = 30;
const MAX_RESOLVE_PAGES = 20;

export interface AdvertiserFilterOption {
  label: string;
  value: string;
}

export interface FetchAdvertisersParams {
  search?: string;
  page?: number;
  includeInactive?: boolean;
}

export interface AdvertiserFilterPageResult {
  items: AdvertiserFilterOption[];
  meta: AdManagerApiResponse<Item[]>['meta'] | undefined;
}

function mapToFilterOptions(response: AdManagerApiResponse<Item[]> | null): AdvertiserFilterOption[] {
  if (!response) {
    return [];
  }

  return response.items.map(({ id, name }) => ({ label: name, value: id }));
}

function mapToFilterPageResult(response: AdManagerApiResponse<Item[]> | null): AdvertiserFilterPageResult {
  return {
    items: mapToFilterOptions(response),
    meta: response?.meta,
  };
}

export async function fetchAllAdvertisersPage({
  search,
  page = 1,
  includeInactive,
}: FetchAdvertisersParams = {}): Promise<AdvertiserFilterPageResult> {
  const response = await apiClient.get<AdManagerApiResponse<Item[]>>(builderApiRoutes.getAdvertisers(), {
    limit: ADVERTISER_FILTER_PAGE_SIZE,
    page,
    search: search || undefined,
    includeInactive,
  });

  return mapToFilterPageResult(response);
}

export async function fetchAssignedAdvertisersPage(
  userId: UserId,
  { search, page = 1 }: FetchAdvertisersParams = {},
): Promise<AdvertiserFilterPageResult> {
  const response = await apiClient.get<AdManagerApiResponse<Item[]>>(
    sharedApiRoutes.getAdvertisersAssignedToUser(userId),
    {
      limit: ADVERTISER_FILTER_PAGE_SIZE,
      page,
      search: search || undefined,
    },
  );

  return mapToFilterPageResult(response);
}

export async function resolveAdvertiserLabelsByIds(
  ids: string[],
  userId: UserId,
  useAssigned: boolean,
): Promise<Record<string, string>> {
  if (ids.length === 0) {
    return {};
  }

  const unresolvedIds = new Set(ids);
  const resolvedLabels: Record<string, string> = {};

  for (let page = 1; page <= MAX_RESOLVE_PAGES && unresolvedIds.size > 0; page++) {
    const pageResult = useAssigned
      ? await fetchAssignedAdvertisersPage(userId, { page })
      : await fetchAllAdvertisersPage({ page });

    pageResult.items.forEach(({ label, value }) => {
      if (unresolvedIds.has(value)) {
        resolvedLabels[value] = label;
        unresolvedIds.delete(value);
      }
    });

    if (pageResult.items.length < ADVERTISER_FILTER_PAGE_SIZE) {
      break;
    }
  }

  unresolvedIds.forEach((id) => {
    resolvedLabels[id] = id;
  });

  return resolvedLabels;
}
